/**
 * Filter bar for the Keyword Search tab.
 *
 * Composes the shared SearchFilters component. The free-text Drug / Mfr
 * inputs that used to render as children here were removed (spec §T8, plan
 * §P6, AC6) — new Drug/Mfr lookups go through the tag/name/manufacturer chip
 * search instead. `drug_name` / `manufacturer` remain in `KeywordFilters`
 * below unchanged, so old links and saved searches keep replaying.
 */
import React from 'react';
import type { Corpus, SearchFilterOptions } from '../types';
import SearchFiltersComponent from './SearchFilters';
import {
  KEYWORD_APPROVAL_LETTER_DISABLED_TOOLTIP, KEYWORD_DOC_SOURCE_DISABLED_TOOLTIP,
  KEYWORD_DOC_SOURCE_OPTIONS, keywordDocSourceEnabled,
} from '../constants/filterConstants';
import type { KeywordDocSource } from '../constants/filterConstants';
import type { SearchRegion } from '../types';

/** Admin "Search in" multi-select (spec 2026-10-16 §6a; admin-only until
 *  K4). Labels is on by default, the last checked option cannot be
 *  unchecked, and a document option the regions rule out (or that origin
 *  scope excludes) is disabled. */
export const KeywordDocSourcesPicker: React.FC<{
  selected: KeywordDocSource[];
  onChange: (next: KeywordDocSource[]) => void;
  regions: SearchRegion[];
  /** Origin scope searches US origin labels alone. */
  labelsOnly?: boolean;
  isLoading?: boolean;
}> = ({ selected, onChange, regions, labelsOnly = false, isLoading = false }) => (
  <div className="flex items-center gap-2 flex-wrap" data-testid="kw-search-in" role="group"
    aria-label="Search in">
    <span className="text-[12px] text-ink-2">Search in</span>
    {KEYWORD_DOC_SOURCE_OPTIONS.map((opt) => {
      const enabled = opt.value === 'labels'
        || (!labelsOnly && keywordDocSourceEnabled(opt.value, regions));
      const checked = enabled && selected.includes(opt.value);
      const lastChecked = checked && selected.filter((v) => v !== opt.value).length === 0;
      return (
        <label key={opt.value}
          className={`flex h-[30px] items-center gap-1.5 rounded-ctl border px-2.5 text-[13px] ${
            checked ? 'border-accent bg-accent-tint text-accent-deep' : 'border-line-input bg-white text-ink'
          } ${enabled ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
          title={enabled ? undefined : opt.value === 'fda_approval_letter'
            ? KEYWORD_APPROVAL_LETTER_DISABLED_TOOLTIP : KEYWORD_DOC_SOURCE_DISABLED_TOOLTIP}>
          <input type="checkbox" className="accent-accent" checked={checked}
            disabled={isLoading || !enabled || lastChecked}
            onChange={(e) => onChange(e.target.checked
              ? [...selected.filter((v) => v !== opt.value), opt.value]
              : selected.filter((v) => v !== opt.value))} />
          {opt.label}
        </label>
      );
    })}
  </div>
);

export interface KeywordFilters extends SearchFilterOptions {
  drug_name: string;
  manufacturer: string;
  pharmacological_class: string[];
}

interface KeywordFilterBarProps {
  filters: KeywordFilters;
  onFiltersChange: (filters: KeywordFilters) => void;
  isLoading?: boolean;
  /** Instant mode: show Labels as the Document Type and grey the rest. */
  labelsOnly?: boolean;
  /** Admin only (spec 2026-10-16 §6a): the "Search in" selection. When set,
   *  the Document Type pill is replaced by ``KeywordDocSourcesPicker`` and
   *  the Labels view (regions, categories) always shows. Non-admins pass
   *  nothing and get exactly today's bar. */
  docSources?: KeywordDocSource[] | undefined;
  onDocSourcesChange?: ((next: KeywordDocSource[]) => void) | undefined;
  /** Origin scope is on: document options are disabled. */
  originScope?: boolean | undefined;
}

const KeywordFilterBar: React.FC<KeywordFilterBarProps> = ({
  filters,
  onFiltersChange,
  isLoading = false,
  labelsOnly = false,
  docSources,
  onDocSourcesChange,
  originScope = false,
}) => {
  // Per-surface corpus derivation (Task 3): the keyword tab's Document
  // Type pill lives on ``filters.searchMode``.  Thread the derived corpus
  // into the inner SearchFilters so the drug-only sub-rows (Region, US /
  // Non-US categories, Additional Filters) hide when the user picks FDA
  // Letters.  Without this, the inner SearchFilters defaulted to
  // ``corpus='labels'`` and the sub-rows stayed visible even after the
  // user clicked FDA Letters in the keyword tab.
  //
  // ``labelsOnly`` shows Labels without writing it, so toggling back to
  // Keyword restores whatever Document Type was picked there. A filter edit
  // made while it's on commits ``searchMode: 'labels'`` along with it — the
  // edit was made against the Labels view (e.g. a Region row that FDA
  // Letters hides).
  const admin = docSources !== undefined;
  const shown = labelsOnly || admin ? { ...filters, searchMode: 'labels' as const } : filters;
  const corpus: Corpus =
    shown.searchMode === 'fda_letters' ? 'fda_letters' : 'labels';

  return (
    <SearchFiltersComponent
      corpus={corpus}
      filters={shown}
      onFiltersChange={(next) => onFiltersChange({ ...shown, ...next })}
      isLoading={isLoading}
      keywordUsOnly
      surface="keyword"
      labelsOnly={labelsOnly}
      inline
      {...(admin && !labelsOnly ? {
        documentsSlot: (
          <KeywordDocSourcesPicker selected={docSources} onChange={(n) => onDocSourcesChange?.(n)}
            regions={filters.regions} labelsOnly={originScope} isLoading={isLoading} />
        ),
        showLetterFilters: docSources.includes('fda_letter') && !originScope,
      } : {})}
    />
  );
};

export default KeywordFilterBar;
